import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SiteMap — an agency's estate as a place, not as a list.
 *
 * WHY THIS REPLACED A TABLE. The Locations widget rendered 101 rows of six
 * columns, and measured against EDD's real data four of those columns carried
 * almost nothing: `source` had ONE distinct value across all 101 rows (and the
 * header above it already said "from the DGS inventory"), `type` was "office"
 * on 83 of 101, the map column read "Pinned" on 91 of 101, and `people` was
 * blank on 81 of 101. Two columns did real work — the site name and the
 * address — and what the address mostly encodes is WHICH CITY, across 65 of
 * them. A column of 101 city names is a map someone forgot to draw.
 *
 * It also cost 5.1 controls per row, so the estate carried ~500 buttons, and
 * at a 77px row you could see 8 of 101 sites in the tile: reading the rest was
 * ten screens of scrolling. Ryan: "I feel like it would best be done on a map
 * and keep it clean and visible. Too much text not anything visually
 * stimulating."
 *
 * WHAT IT DOES NOT CLAIM. A map answers "where", which is not always the
 * question — the rep often wants "which of these matter", and the natural
 * weight for that is headcount, which is absent on 80% of these rows. So size
 * here encodes SITE COUNT, a number we actually have for every city, and the
 * widget never implies a ranking it cannot support. Addresses live one click
 * away in the full view, which is also where editing belongs.
 *
 * ONE DOT PER CITY, NOT PER SITE. 101 marks in a 430px tile over a state this
 * tall collapses the Sacramento and Bay Area clusters into a single blob —
 * the same failure as a scatter whose marks overlap. Aggregating to the city
 * (65 marks) is both more readable and more honest: "six offices in Sacramento"
 * is the fact, and six coincident dots were only ever a rendering of it.
 *
 * Presentational: data via props, no fetching, colours from CSS variables.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CALIFORNIA_RINGS } from './californiaOutline.js';
/*
 * The projection, written out rather than imported.
 *
 * `d3-geo` would do this, but packages/ui ships into the Apps Script bundle
 * where every kilobyte is a paste into a tenant editor, and this is the only
 * place in the package that needs a projection. Albers conic equal-area on
 * California's own standard parallels — 34°N and 40.5°N, the parameters of
 * EPSG:3310, the projection the state publishes its own maps in. Equal-area
 * matters here for the same reason it matters on any dot map: an area-
 * distorting projection makes the same cluster look denser in the south than
 * in the north.
 */
const PHI1 = (34 * Math.PI) / 180;
const PHI2 = (40.5 * Math.PI) / 180;
const LON0 = (-120 * Math.PI) / 180;
const PHI0 = (0 * Math.PI) / 180;
const N = (Math.sin(PHI1) + Math.sin(PHI2)) / 2;
const C = Math.cos(PHI1) ** 2 + 2 * N * Math.sin(PHI1);
const RHO0 = Math.sqrt(C - 2 * N * Math.sin(PHI0)) / N;
function project(lon, lat) {
    const phi = (lat * Math.PI) / 180;
    const lam = (lon * Math.PI) / 180;
    const rho = Math.sqrt(Math.max(C - 2 * N * Math.sin(phi), 0)) / N;
    const theta = N * (lam - LON0);
    return [rho * Math.sin(theta), RHO0 - rho * Math.cos(theta)];
}
/*
 * THE DRAWING IS A FIXED SHAPE, SO IT IS NOT MEASURED.
 *
 * The first version measured its own box and drew into a pixel-space viewBox,
 * copying CeilingGauge. That was wrong here, and it shipped a render loop:
 * the plot's height came from its width through an aspect-ratio, the widget
 * body scrolls, and a scrollbar appearing narrows the element — which shortens
 * the height, which removes the scrollbar, which widens it again. The box
 * oscillated between two sizes, so the identity bail-out never fired and React
 * threw "Maximum update depth exceeded". The guard was right and the thing it
 * guarded was unwinnable.
 *
 * The gauge has to measure because its marks must stay ROUND while its axes
 * stretch independently. A map has no such conflict: every part of it scales
 * together. So the viewBox is constant, `preserveAspectRatio` does the fitting,
 * and there is no measurement, no ResizeObserver and no feedback path at all.
 */
const PAD = 14;
const VB_W = 1000;
const RINGS_XY = CALIFORNIA_RINGS.map((ring) => ring.map(([lon, lat]) => project(lon, lat)));
const FLAT = RINGS_XY.flat();
const MIN_X = Math.min(...FLAT.map((p) => p[0]));
const MAX_X = Math.max(...FLAT.map((p) => p[0]));
const MIN_Y = Math.min(...FLAT.map((p) => p[1]));
const MAX_Y = Math.max(...FLAT.map((p) => p[1]));
const SPAN_X = MAX_X - MIN_X;
const SPAN_Y = MAX_Y - MIN_Y;
/** California's own proportions decide the viewBox, so nothing is distorted. */
const SCALE = (VB_W - PAD * 2) / SPAN_X;
const VB_H = Math.round(SPAN_Y * SCALE + PAD * 2);
/*
 * Y IS FLIPPED, AND IT HAS TO BE.
 *
 * Albers returns `rho0 - rho * cos(theta)`, which GROWS as you go north. SVG's
 * y grows DOWNWARD. Map one straight onto the other and California renders
 * upside down — which is exactly what shipped: Eureka at the bottom, San Diego
 * at the top. d3 hides this because its projection machinery negates y when it
 * applies scale and translate; writing the projection out means owning the
 * flip too.
 *
 * Nothing measurable caught it. The marks were round, the counts were right,
 * the box fit its cell. It took someone looking at the picture — which is the
 * argument for looking at the picture.
 */
const toVB = (p) => [
    (p[0] - MIN_X) * SCALE + PAD,
    (MAX_Y - p[1]) * SCALE + PAD,
];
const STATE_PATH = RINGS_XY
    .map((ring) => ring.map(toVB).map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join('') + 'Z')
    .join('');
/** Cities close enough to be one mark at tile scale share a key. */
function groupSites(sites) {
    const byCity = new Map();
    for (const s of sites || []) {
        const lat = Number(s.latitude);
        const lon = Number(s.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon))
            continue;
        // Fall back to the coordinate itself when the city is blank, so a site is
        // never silently dropped for want of a label.
        const city = (s.city || '').trim() || `${lat.toFixed(2)}, ${lon.toFixed(2)}`;
        const key = city.toLowerCase();
        const g = byCity.get(key);
        if (g) {
            g.count += 1;
            g.sites.push(s);
            if (s.siteType === 'field_office')
                g.fieldOffices += 1;
            // The mark sits at the mean of its sites, not at the first one.
            g.lat += (lat - g.lat) / g.count;
            g.lon += (lon - g.lon) / g.count;
        }
        else {
            byCity.set(key, {
                key, city, count: 1, sites: [s],
                fieldOffices: s.siteType === 'field_office' ? 1 : 0,
                lat, lon,
            });
        }
    }
    // Biggest last so the largest marks paint on top of their neighbours.
    return [...byCity.values()].sort((a, b) => a.count - b.count);
}
export const SiteMap = ({ sites, coverage, fill = false, onPick, onOpenAll, }) => {
    const svgRef = useRef(null);
    const [hit, setHit] = useState(null);
    /** Where across the plot the tooltip should sit, 0-100. */
    const [tipX, setTipX] = useState(50);
    const { marks, maxCount, clusterOf, clusters } = useMemo(() => {
        const groups = groupSites(sites);
        const max = groups.reduce((m, g) => Math.max(m, g.count), 0) || 1;
        const ms = groups.map((g) => {
            const [x, y] = toVB(project(g.lon, g.lat));
            // Area encodes count, so the radius is a square root. In viewBox
            // units, which scale with the drawing like everything else.
            const r = 9 + Math.sqrt(g.count / max) * 16;
            return { g, x, y, r, sx: x, sy: y };
        });
        /*
         * CITIES THAT COVER EACH OTHER, AND WHAT TO DO ABOUT IT.
         *
         * Aggregating sites to their city fixed the worst overlap, but it cannot
         * fix all of it: Sacramento, West Sacramento and Rancho Cordova are
         * genuinely a few miles apart, so at state scale their marks sit on top of
         * one another and the one underneath is unreachable at any pointer
         * position. Ryan: "I need an intelligent way when there is a cluster of
         * offices that overlaps, for me to hover on them and them spread out so I
         * can select the one I want."
         *
         * WHY SPREAD RATHER THAN THE ALTERNATIVES. Zooming on hover moves the
         * whole map under the pointer and loses the national shape that is the
         * reason for a map at all. A fisheye lens distorts continuously, so the
         * target keeps moving as you approach it — the worst property a click
         * target can have. Collapsing the cluster into a "3" badge that opens a
         * list is reliable but answers the question with a menu, which is the text
         * this widget exists to get away from.
         *
         * Spreading keeps every mark its own size and colour, moves it a bounded,
         * predictable distance, and draws a leader line back to where it really
         * is — so the map never lies about geography while it is being used. It is
         * also the interaction people have already met in every mapping tool.
         *
         * The grouping is connected components over "these two circles touch",
         * not a fixed grid: a chain of three marks each overlapping the next is
         * one cluster, which is what the eye sees.
         */
        const touch = (a, b) => {
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            return Math.hypot(dx, dy) < a.r + b.r + 6;
        };
        const cOf = new Array(ms.length).fill(-1);
        const cls = [];
        for (let i = 0; i < ms.length; i += 1) {
            if (cOf[i] !== -1)
                continue;
            const members = [];
            const queue = [i];
            cOf[i] = cls.length;
            while (queue.length) {
                const k = queue.pop();
                members.push(k);
                for (let j = 0; j < ms.length; j += 1) {
                    if (cOf[j] === -1 && touch(ms[k], ms[j])) {
                        cOf[j] = cls.length;
                        queue.push(j);
                    }
                }
            }
            const ax = members.reduce((s, k) => s + ms[k].x, 0) / members.length;
            const ay = members.reduce((s, k) => s + ms[k].y, 0) / members.length;
            // The ring has to be big enough that the spread marks clear each other,
            // so it grows with the count and with the largest mark in the group.
            let reach = 0;
            if (members.length > 1) {
                const rMax = Math.max(...members.map((k) => ms[k].r));
                reach = Math.max(rMax + 26, (members.length * (2 * rMax + 10)) / (2 * Math.PI));
                members
                    // Clockwise from the top, in a stable order, so the same cluster
                    // always fans out the same way.
                    .sort((p, q) => (ms[p].g.city < ms[q].g.city ? -1 : 1))
                    .forEach((k, idx) => {
                    const a = -Math.PI / 2 + (idx * 2 * Math.PI) / members.length;
                    ms[k].sx = ax + Math.cos(a) * reach;
                    ms[k].sy = ay + Math.sin(a) * reach;
                });
            }
            cls.push({ members, ax, ay, reach });
        }
        return { maxCount: max, marks: ms, clusterOf: cOf, clusters: cls };
    }, [sites]);
    /** The cluster currently fanned out, if any. */
    const [openCluster, setOpenCluster] = useState(null);
    const isOpen = (i) => openCluster != null && clusterOf[i] === openCluster;
    /** Where a mark actually sits right now — spread if its cluster is open. */
    const posOf = useCallback((i) => (openCluster != null && clusterOf[i] === openCluster
        ? { x: marks[i].sx, y: marks[i].sy }
        : { x: marks[i].x, y: marks[i].y }), [marks, clusterOf, openCluster]);
    const nearest = useCallback((clientX, clientY) => {
        const el = svgRef.current;
        if (!el || marks.length === 0)
            return null;
        // getScreenCTM knows the viewBox AND the letterboxing preserveAspectRatio
        // introduces; scaling by the bounding rect alone would be off by the
        // margins whenever the element is not exactly the drawing's ratio.
        const ctm = el.getScreenCTM();
        if (!ctm)
            return null;
        const inv = ctm.inverse();
        const pt = el.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const u = pt.matrixTransform(inv);
        let best = null;
        let bestD = 46 * 46;
        marks.forEach((m, i) => {
            // Test where the mark IS, which is its spread position while its cluster
            // is fanned out. Testing the true position instead would fan the cluster
            // open and then refuse to let you click any of it — the marks would have
            // moved out from under their own hit areas.
            const p = posOf(i);
            const dx = p.x - u.x;
            const dy = p.y - u.y;
            const d = dx * dx + dy * dy;
            if (d <= bestD) {
                bestD = d;
                best = i;
            }
        });
        return best;
    }, [marks, posOf]);
    /** Pointer position as a percentage across the plot, for the tooltip. */
    const trackTip = useCallback((clientX) => {
        const el = svgRef.current;
        if (!el)
            return;
        const r = el.getBoundingClientRect();
        if (r.width <= 0)
            return;
        setTipX(Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100)));
    }, []);
    useEffect(() => { setHit(null); }, [marks]);
    const placed = marks.reduce((n, m) => n + m.g.count, 0);
    const cities = marks.length;
    const total = coverage?.total ?? (sites || []).length;
    const missing = Math.max(0, total - placed);
    if (!sites || sites.length === 0) {
        return (_jsx("div", { className: `chs__empty${fill ? ' chs__empty--fill' : ''}`, children: "No sites on record for this agency." }));
    }
    const active = hit != null ? marks[hit] : null;
    return (_jsxs("div", { className: `smap${fill ? ' chfill' : ''}`, children: [_jsxs("p", { className: "smap-read", children: [_jsx("strong", { children: placed }), " ", placed === 1 ? 'site' : 'sites', " across", ' ', _jsx("strong", { children: cities }), " ", cities === 1 ? 'city' : 'cities', missing > 0 && _jsxs(_Fragment, { children: [" \u00B7 ", missing, " without a location"] }), coverage?.humanVerified === 0 && total > 0 && _jsx(_Fragment, { children: " \u00B7 none confirmed by a person" })] }), _jsxs("div", { className: `smap-plot${fill ? ' smap-plot--fill' : ''}`, children: [_jsxs("svg", { ref: svgRef, viewBox: `0 0 ${VB_W} ${VB_H}`, preserveAspectRatio: "xMidYMid meet", width: "100%", height: "100%", role: "img", "aria-label": `${placed} sites across ${cities} cities in California`, className: "smap-svg", children: [_jsx("path", { className: "smap-state", d: STATE_PATH }), openCluster != null && clusters[openCluster]?.members.length > 1
                                && clusters[openCluster].members.map((k) => (_jsx("line", { className: "smap-lead", x1: marks[k].x, y1: marks[k].y, x2: marks[k].sx, y2: marks[k].sy }, `lead-${marks[k].g.key}`))), marks.map((m, i) => {
                                const open = isOpen(i) && clusters[clusterOf[i]].members.length > 1;
                                return (
                                /* The mark travels by TRANSFORM rather than by cx/cy: a
                                   transform transitions reliably everywhere, and the circle
                                   keeps one identity as it moves, so focus and the tooltip do
                                   not flicker mid-flight. */
                                _jsxs("g", { className: `smap-mark${open ? ' smap-mark--out' : ''}`, style: open
                                        ? { '--dx': `${m.sx - m.x}px`, '--dy': `${m.sy - m.y}px` }
                                        : undefined, children: [_jsx("circle", { className: `smap-dot${m.g.fieldOffices === m.g.count ? ' smap-dot--field' : ''}${hit === i ? ' smap-dot--on' : ''}`, cx: m.x, cy: m.y, r: m.r, tabIndex: onPick ? 0 : undefined, role: onPick ? 'button' : undefined, onFocus: () => {
                                                setHit(i);
                                                // Focus fans the cluster too, or the keyboard can reach a
                                                // mark it can never see.
                                                setOpenCluster(clusters[clusterOf[i]].members.length > 1 ? clusterOf[i] : null);
                                                setTipX((m.x / VB_W) * 100);
                                            }, onBlur: () => setHit((h) => (h === i ? null : h)), onKeyDown: onPick ? (e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    onPick(m.g);
                                                }
                                            } : undefined, children: _jsx("title", { children: `${m.g.city} — ${m.g.count} ${m.g.count === 1 ? 'site' : 'sites'}` }) }), open && (_jsx("text", { className: "smap-mark__label", x: m.x, y: m.y + m.r + 15, textAnchor: "middle", children: m.g.city }))] }, m.g.key));
                            }), _jsx("rect", { className: `smap-hit${onPick ? ' chpick' : ''}`, x: 0, y: 0, width: VB_W, height: VB_H, fill: "transparent", 
                                /*
                                 * The whole interaction, in three lines and no timers.
                                 *
                                 * `nearest` already tests the spread positions while a cluster is
                                 * open, so the open cluster is stickier than the collapsed map: to
                                 * leave it you have to move further than 46 units from every one of
                                 * its fanned-out marks, which is exactly the "grace area" a
                                 * hover-to-expand needs. Walking from one mark to its neighbour
                                 * inside the fan never collapses it, and a timer — the usual way
                                 * people buy that grace — would make the map feel laggy instead.
                                 */
                                onPointerMove: (e) => {
                                    const i = nearest(e.clientX, e.clientY);
                                    setHit(i);
                                    trackTip(e.clientX);
                                    if (i == null)
                                        setOpenCluster(null);
                                    else
                                        setOpenCluster(clusters[clusterOf[i]].members.length > 1 ? clusterOf[i] : null);
                                }, onPointerLeave: () => { setHit(null); setOpenCluster(null); }, onClick: onPick ? (e) => {
                                    const i = nearest(e.clientX, e.clientY);
                                    if (i != null)
                                        onPick(marks[i].g);
                                } : undefined })] }), active && (_jsxs("div", { className: `smap-tip${tipX > 50 ? ' smap-tip--left' : ''}${active.y < VB_H / 2 ? ' smap-tip--low' : ''}`, style: { '--tip-x': `${tipX}%` }, children: [_jsx("span", { className: "smap-tip__city", children: active.g.city }), _jsxs("span", { className: "smap-tip__count", children: [active.g.count, " ", active.g.count === 1 ? 'site' : 'sites', active.g.fieldOffices > 0 && ` · ${active.g.fieldOffices} field`] }), onPick && _jsx("span", { className: "smap-tip__cta", children: "Click for the addresses" })] }))] }), _jsxs("div", { className: "smap-foot", children: [_jsxs("span", { className: "smap-key", children: [_jsx("span", { className: "smap-key__dot" }), " office", _jsx("span", { className: "smap-key__dot smap-key__dot--field" }), " field office"] }), _jsxs("span", { className: "smap-key__scale", children: ["area = sites in that city, up to ", maxCount] }), onOpenAll && (_jsxs("button", { type: "button", className: "smap-all", onClick: onOpenAll, children: ["All ", total, " with addresses"] }))] })] }));
};
export default SiteMap;
